import { getEventPropsBuilder, toSnakeCase } from '../../utils';
import type { AmplitudeEventKeys } from '../../amplitude-properties.types';
import type { TrackingEvent } from '@/tracking/tracking-manager';

type TrackingData = {
    categoryUid?: string | null;
    zoneName?: string | null;
    isZoneDynamicallyOrdered?: boolean;
    module?: string | null;
    gameId?: string | null;
    gameName?: string | null;
    gameProvider?: string | null;
    gamePosition?: string | null;
    activeCount?: string | null;
};

type EventConfig = {
    event: 'Game Info Modal Opened' | 'Game Information Page Viewed' | 'Game Selected';
    module: string;
    getCategory: (data: TrackingData) => string;
    getZoneName: (data: TrackingData) => string;
    getIsZoneDynamicallyOrdered?: (data: TrackingData) => boolean;
    getGameId?: (data: TrackingData) => string;
    getGameName?: (data: TrackingData) => string;
    getGameProvider?: (data: TrackingData) => string;
    getGamePosition?: (data: TrackingData) => string;
    getActiveCount?: (data: TrackingData) => string;
};

const EVENT_CONFIGS: Record<string, EventConfig> = {
    modalOpened: {
        event: 'Game Info Modal Opened',
        module: 'game_info_modal',
        getCategory: (data) => toSnakeCase(data.categoryUid || ''),
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getGameId: (data) => data.gameId || '',
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getGameProvider: (data) => toSnakeCase(data.gameProvider || ''),
        getGamePosition: (data) => toSnakeCase(data.gamePosition || '')
    },
    modalSelectGame: {
        event: 'Game Selected',
        module: 'game_info_modal',
        getCategory: (data) => toSnakeCase(data.categoryUid || ''),
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getIsZoneDynamicallyOrdered: (data) => data.isZoneDynamicallyOrdered ?? false,
        getActiveCount: (data) => data.activeCount || ''
    },
    pageOpened: {
        event: 'Game Information Page Viewed',
        module: 'game_info_page',
        getCategory: () => 'game_info_cta',
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getGameId: (data) => data.gameId || '',
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getGameProvider: (data) => toSnakeCase(data.gameProvider || ''),
        getGamePosition: (data) => toSnakeCase(data.gamePosition || '')
    },
    pageSelectGame: {
        event: 'Game Selected',
        module: 'game_info_page',
        getCategory: () => 'game_info_cta',
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getIsZoneDynamicallyOrdered: (data) => data.isZoneDynamicallyOrdered ?? false,
        getGameId: (data) => data.gameId || '',
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getActiveCount: (data) => data.activeCount || ''
    }
};

const buildEventProps = getEventPropsBuilder((config: EventConfig, data: TrackingData): Partial<AmplitudeEventKeys> => {
    const baseProps: Partial<AmplitudeEventKeys> = {
        event: config.event,
        Module: data.module || config.module,
        Category: config.getCategory(data),
        'Zone Name': config.getZoneName(data),
        'Game ID': config.getGameId?.(data),
        'Game Name': config.getGameName?.(data),
        'Game Provider': config.getGameProvider?.(data),
        Position: config.getGamePosition?.(data),
        'Number Of Actives': config.getActiveCount?.(data)
    };

    if (config.getIsZoneDynamicallyOrdered) {
        baseProps['Is Zone Dynamically Ordered'] = config.getIsZoneDynamicallyOrdered(data);
    }

    return baseProps;
});

const extractTrackingData = (element: HTMLElement | null): TrackingData | null => {
    if (!element) {
        return null;
    }

    const zone = element.closest('[data-t-zoneuid]');
    const category = element.closest('[data-t-categoryuid]');
    const categoryUid = category?.getAttribute('data-t-categoryuid');
    const zoneName = zone?.getAttribute('data-t-zonename');
    const zoneModule = element.closest('[data-t-zonemodule]')?.getAttribute('data-t-zonemodule');

    const game = element.closest('[data-t-gameuid]');
    const gameId = game?.getAttribute('data-t-gameuid');

    let gameName = game?.getAttribute('data-t-gamename');
    if (!gameName) {
        gameName = element.closest('[data-t-gamename]')?.getAttribute('data-t-gamename');
    }

    let gameProvider = game?.getAttribute('data-t-gameprovider');
    if (!gameProvider) {
        gameProvider = element.closest('[data-t-gameprovider]')?.getAttribute('data-t-gameprovider');
    }

    const gamePosition = game?.getAttribute('data-t-gameposition');

    return {
        categoryUid,
        zoneName,
        gameId,
        gameName,
        gameProvider,
        gamePosition,
        module: zoneModule
    };
};

export const trackGameInfoEvent = (
    eventType: 'modalOpened' | 'modalSelectGame' | 'pageOpened' | 'pageSelectGame',
    source?: TrackingData | { current: HTMLElement | null } | null,
    additionalData?: Partial<TrackingData>
): { event: TrackingEvent; trackingData: TrackingData | null } => {
    const config = EVENT_CONFIGS[eventType];
    if (!config) {
        console.warn(`[Tracking] Unknown game info event type: ${eventType}`);
    }

    let trackingData: TrackingData | null = null;

    if (source) {
        if ('current' in source) {
            trackingData = extractTrackingData(source.current);
        } else {
            trackingData = source;
        }
    }

    const data = { categoryUid: null, zoneName: null, ...trackingData, ...additionalData };
    const eventProps = buildEventProps(config, data);

    return {
        event: {
            destination: 'amplitude',
            eventProps
        },
        trackingData
    };
};
